import { FC, useState } from 'react';

import { useFormikContext } from 'formik';
import { useDropzone } from 'react-dropzone';
import cn from 'classnames';
import { debounce, isEmpty, uniqBy } from 'lodash';
import PropTypes from 'prop-types';
import c, { COMPOSER_MODES, SIZE } from 'utils/constants';
import { AttachmentsTooLargeModal, Button } from 'ui/components/common';
import { convertToMegabytes } from 'utils/helpers';
import EmailAddressMultiselect from 'ui/components/form/EmailAddressMultiSelect';
import { useProfileContext } from 'utils/providers/Profile';
import { Lead } from 'utils/types';
import { EMAIL_FORM } from 'modules/account/contact/forms/schema';
import { Textarea } from 'ui/components/form';
import { useGetLeads } from 'modules/account/contact/hooks';
import SelectEmailTemplateModal from 'modules/account/resource/components/SelectEmailTemplateModal';
import { CreateSimpleLeadModal } from 'modules/account/contact/components';

import UploadListItem from './UploadListItem';
import useModal, { useExternalModal } from '../Modal/useModal';

const FromEmailAddressSelector = ({
  currentEmail: { fullName = '', emailAddress = '' },
}) => (
  <div className="flex items-center w-full gap-1">
    <div>{fullName}</div>
    <div className="text-blue font-semibold leading-4 tracking-[0.06em]">
      {`(${emailAddress})`}
    </div>
  </div>
);

FromEmailAddressSelector.propTypes = {
  currentEmail: PropTypes.shape({
    fullName: PropTypes.string,
    emailAddress: PropTypes.string,
  }).isRequired,
};

type ComposerEmailFormProps = {
  size?: SIZE;
  mode?: COMPOSER_MODES;
  attachments: any[];
  setAttachments: React.Dispatch<React.SetStateAction<any[]>>;
  isDisabled?: boolean;
};

const ComposerEmailForm: FC<ComposerEmailFormProps> = ({
  size = SIZE.Small,
  attachments,
  setAttachments,
  isDisabled = true,
}) => {
  const { state } = useProfileContext();
  const [keyword, setKeyword] = useState('');
  const { showModal: showExternalModal, hideModal: hideExternalModal } =
    useExternalModal();
  const { data: leadsData, isLoading } = useGetLeads({ keyword });

  const leadOptions = leadsData?.data?.map((lead: Lead) => ({
    label: lead?.fullName ?? `${lead?.firstName ?? ''} ${lead?.lastName ?? ''}`,
    value: lead.emailAddress,
    metadata: lead,
  }));

  const {
    values,
    errors,
    dirty,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    setValues,
    isValid,
    isSubmitting,
  } = useFormikContext<EMAIL_FORM>();

  const { showModal, hideModal } = useModal();

  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);

  const _showCc = () => setShowCc(true);
  const _showBcc = () => setShowBcc(true);

  const showAttachmentsTooLargeModal = (fileSize: string) => {
    showModal({
      title: 'Attachments',
      disableOutsideClick: true,
      content: <AttachmentsTooLargeModal fileSize={fileSize} />,
    });
  };

  const showSelectTemplateModal = () => {
    showModal({
      title: 'Select Template',
      disableOutsideClick: true,
      resetPadding: true,
      content: (
        <SelectEmailTemplateModal
          onClick={(template) => {
            setFieldValue('text', template.body);
            setFieldValue('subject', template.subject);
            hideModal();
          }}
        />
      ),
      size: 'xl',
    });
  };

  const handleToggleLead = (inputValue: string) => {
    showExternalModal({
      title: 'Create Lead',
      disableOutsideClick: true,
      content: (
        <CreateSimpleLeadModal
          initValues={{ emailAddress: inputValue }}
          afterCreate={(lead) => {
            setValues((preVal) => ({
              ...preVal,
              to: [
                ...preVal.to,
                { label: lead.fullName, value: lead.emailAddress },
              ],
            }));
            hideExternalModal();
          }}
          onCancel={hideExternalModal}
        />
      ),
    });
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    maxSize: c.MAX_UPLOAD_SIZE,
    noClick: true,
    noKeyboard: true,
    onDrop: (_acceptedFiles) => {
      setAttachments((prevVal: any[]) =>
        uniqBy([...prevVal, ..._acceptedFiles], 'name'),
      );
    },
    onDropRejected: (fileRejections) => {
      const totalSize = fileRejections.reduce(
        (acc, { errors: _errors, file }) => {
          if (_errors[0].code === 'file-too-large') {
            return acc + file.size;
          }
          return acc;
        },
        0,
      );
      if (fileRejections[0].errors[0].code === 'file-too-large') {
        showAttachmentsTooLargeModal(convertToMegabytes(totalSize));
      }
    },
  });

  const removeAttachment = (name: string) => {
    setAttachments((prevVal: any[]) =>
      prevVal.filter((val) => !(val.name === name)),
    );
  };

  const files = !!attachments?.length && (
    <div
      className={cn(
        'space-y-[6px]',
        'max-h-[200px]',
        'w-full',
        'overflow-x-hidden',
        'overflow-y-auto',
        attachments.length > 3 && 'pr-1',
      )}
    >
      {attachments?.map((file: File) => (
        <UploadListItem
          key={file.name}
          file={{
            ...file,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
          }}
          size={SIZE.Large}
          showSize
          onRemove={() => removeAttachment(file.name)}
          isUploaded={false}
        />
      ))}
    </div>
  );

  return (
    <form
      onSubmit={handleSubmit}
      className="px-[22px] pt-[22px] [padding-bottom: 500px]"
    >
      <div
        className={cn(
          'flex',
          'items-center',
          'gap-3',
          'first:pt-[0px]',
          'pt-[14px]',
          'pb-[14px]',
          'border-b-[0.5px]',
          'border-b-separator-primary',
        )}
      >
        <div className="font-normal text-sm leading-[22px] tracking-[0.04em]">
          To
        </div>
        <EmailAddressMultiselect
          isLoading={isLoading}
          onInputChange={debounce((value) => {
            setKeyword(value);
          }, 500)}
          isDisabled={isDisabled}
          value={values.to}
          onChange={setFieldValue}
          onBlur={() => setFieldTouched('to')}
          name="to"
          options={leadOptions}
          onCreateOption={(val) => handleToggleLead(val)}
        />
      </div>
      <div
        className={cn(
          'flex',
          'items-center',
          'text-sm',
          'gap-3',
          'first:pt-[0px]',
          'pt-[14px]',
          'pb-[14px]',
          'border-b-[0.5px]',
          'border-b-separator-primary',
        )}
      >
        <div className="font-normal leading-[22px] tracking-[0.04em]">From</div>
        <FromEmailAddressSelector
          currentEmail={{
            fullName: state?.profile?.fullName,
            emailAddress: values.from,
          }}
        />
        <div className="flex gap-4 justify-self-end">
          {!showCc && (
            <button
              type="button"
              onClick={_showCc}
              className="text-sm font-semibold transition hover:text-info-base"
            >
              Cc
            </button>
          )}
          {!showBcc && (
            <button
              type="button"
              onClick={_showBcc}
              className="text-sm font-semibold transition hover:text-info-base"
            >
              Bcc
            </button>
          )}
        </div>
      </div>
      {showCc && (
        <div
          className={cn(
            'flex',
            'items-center',
            'gap-3',
            'first:pt-[0px]',
            'pt-[14px]',
            'pb-[14px]',
            'border-b-[0.5px]',
            'border-b-separator-primary',
          )}
        >
          <div className="font-normal text-sm leading-[22px] tracking-[0.04em]">
            Cc
          </div>
          <EmailAddressMultiselect
            isLoading={isLoading}
            onInputChange={debounce((value) => {
              setKeyword(value);
            }, 500)}
            value={values.cc}
            onChange={setFieldValue}
            onBlur={() => setFieldTouched('cc')}
            name="cc"
            options={leadOptions}
          />
        </div>
      )}
      {showBcc && (
        <div
          className={cn(
            'flex',
            'items-center',
            'gap-3',
            'first:pt-[0px]',
            'pt-[14px]',
            'pb-[14px]',
            'border-b-[0.5px]',
            'border-b-separator-primary',
          )}
        >
          <div className="font-normal text-sm leading-[22px] tracking-[0.04em]">
            Bcc
          </div>
          <EmailAddressMultiselect
            isLoading={isLoading}
            onInputChange={debounce((value) => {
              setKeyword(value);
            }, 500)}
            value={values.bcc}
            onChange={setFieldValue}
            onBlur={() => setFieldTouched('bcc')}
            name="bcc"
            options={leadOptions}
          />
        </div>
      )}
      <div
        className={cn(
          'flex',
          'gap-3',
          'first:pt-[0px]',
          'pt-[14px]',
          'pb-[14px]',
          'border-b-[0.5px]',
          'border-b-separator-primary',
        )}
      >
        <input
          name="subject"
          value={values.subject}
          onChange={handleChange}
          onBlur={handleBlur}
          type="text"
          placeholder="Subject"
          className={cn(
            'font-normal',
            'w-full',
            'text-sm',
            'leading-[22px]',
            'tracking-[0.04em]',
            'focus:outline-none',
            'border-b-separator-primary',
          )}
        />
      </div>
      <div className="py-[14px]">
        <div {...getRootProps()} className={cn(size)}>
          <input {...getInputProps()} />
          <Textarea
            isEmail
            richText
            name="email"
            placeholder="Write your message here"
            onChange={(d) => {
              setFieldValue('text', d);
            }}
            value={values.text}
            onClickAttachButton={open}
            onClickShowTemplates={showSelectTemplateModal}
            className={cn('rounded-[6px] border-separator-primary')}
          >
            {files}
          </Textarea>
          {isDragActive && (
            <div
              className={cn(
                'absolute inset-y-0 left-0',
                'bg-fill-tertiary',
                'bg-opacity-90',
                'h-full w-full',
                'grid place-items-center',
                'border-2 border-dashed border-secondary-base',
              )}
            >
              <div className="text-lg">Drop Files Here</div>
            </div>
          )}
        </div>
        <div
          className={cn(
            'flex justify-end gap-3',
            'px-2.5 pt-5 mt-5',
            'border-t-[0.5px] border-separator-primary',
          )}
        >
          <Button
            type="submit"
            disabled={!isValid || !dirty || !isEmpty(errors) || isSubmitting}
            className="secondary sm"
            label="SEND"
          />
        </div>
      </div>
    </form>
  );
};

export default ComposerEmailForm;
